import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
    Building,
    CheckCircle,
    Clock,
    DollarSign,
    FileText,
    Headphones,
    Home,
    Mail,
    MessageSquare,
    Mic,
    PhoneCall,
    PieChart,
    TrendingUp,
    Utensils,
    X,
} from 'lucide-react'

import { trackMarketingEvent } from '@/api/deferredAnalytics'
import { track } from '@/api/tracking'
import { NewsletterSubscribe } from '@/features/newsletter/NewsletterSubscribe'
import { OFFICIAL_SALES_PHONE, toOfficialSalesTelHref } from '@/lib/officialContact'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES, SITE_ORIGIN } from '@/navigation/constants'
import { absoluteLocalizedUrl } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'

import { LeadForm } from '../components/LeadForm'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'

const OG_IMAGE = `${SITE_ORIGIN}/escandallo-restaurante.webp`

export function BusinessPage() {
    const { t } = useTranslation()
    const locale = useLandingLocale()
    const title = `Tipi | ${t('hero.title')} ${t('hero.highlight')}`
    const description = t('hero.description')
    const canonicalUrl = absoluteLocalizedUrl(ROUTES.invest, locale)

    // Tracking refs for each section
    const problemsSectionRef = useRef<HTMLElement>(null)
    const howItWorksSectionRef = useRef<HTMLElement>(null)
    const benefitsSectionRef = useRef<HTMLElement>(null)
    const marketSectionRef = useRef<HTMLElement>(null)
    const teamSectionRef = useRef<HTMLElement>(null)
    const pricingSectionRef = useRef<HTMLElement>(null)
    const formSectionRef = useRef<HTMLElement>(null)

    // Track section views using IntersectionObserver
    useEffect(() => {
        const createObserver = (ref: React.RefObject<HTMLElement>, eventName: string) => {
            if (ref.current) {
                const observer = new IntersectionObserver(
                    ([entry]) => {
                        if (entry.isIntersecting) {
                            track(eventName)
                            // Track form viewed event with gtag for Form section
                            if (eventName === 'Form') {
                                trackMarketingEvent('Form Viewed', {
                                    event_category: 'engagement',
                                    event_label: 'form_section',
                                })
                            }
                        }
                    },
                    { threshold: 0.5 },
                )
                observer.observe(ref.current)
                return () => observer.disconnect()
            }
        }

        const cleanup = [
            createObserver(problemsSectionRef, 'Problems'),
            createObserver(howItWorksSectionRef, 'How It Works'),
            createObserver(benefitsSectionRef, 'Benefits'),
            createObserver(marketSectionRef, 'Market'),
            createObserver(teamSectionRef, 'Team'),
            createObserver(pricingSectionRef, 'Pricing'),
            createObserver(formSectionRef, 'Form'),
        ].filter(Boolean)

        return () => {
            cleanup.forEach(cleanupFn => cleanupFn?.())
        }
    }, [])

    const scrollToContact = () => {
        formSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    return (
        <div className='flex min-h-screen flex-col'>
            <LocaleSeoLinks path={ROUTES.invest} locale={locale} />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <meta property='og:type' content='website' />
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:image' content={OG_IMAGE} />
                <meta name='twitter:card' content='summary_large_image' />
                <meta name='twitter:url' content={canonicalUrl} />
                <meta name='twitter:title' content={title} />
                <meta name='twitter:description' content={description} />
                <meta name='twitter:image' content={OG_IMAGE} />
            </PageHelmet>

            <header className='border-neutral-200 sticky top-0 z-50 flex h-16 items-center border-b bg-black px-4 lg:px-6'>
                <a className='flex items-center justify-center' href='#'>
                    <img src='/TIPI-tipografico.webp' alt='Tipi' className='h-16 w-auto -translate-x-1' />
                </a>
                <div className='ml-auto flex items-center space-x-4'>
                    <Button onClick={scrollToContact} className='rounded-md bg-primary hover:bg-primary/90'>
                        {t('contact.ctaButton')}
                    </Button>
                </div>
            </header>

            <main className='flex-1'>
                {/* Hero Section */}
                <section className='relative flex h-screen w-full items-center overflow-hidden px-6 py-12 md:h-fit md:py-24 lg:py-32'>
                    <div className='absolute inset-0 z-0'>
                        <img
                            src='/escandallo-restaurante.webp'
                            alt='Chef en acción'
                            className='h-full w-full object-cover opacity-20'
                            loading='eager'
                        />
                    </div>
                    <div className='container relative z-10 px-4 md:px-6'>
                        <div className='grid grid-cols-1 items-center gap-8'>
                            <div className='space-y-4'>
                                <img src='/TIPI-tipografico.webp' alt='Tipi' className='h-[250px] w-auto' />
                                <h1 className='text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl'>
                                    {t('hero.title')} <span className='text-primary'>{t('hero.highlight')}</span>
                                </h1>
                                <p className='text-neutral-700 max-w-[900px] md:text-xl'>{t('hero.description')}</p>
                                <Button
                                    onClick={scrollToContact}
                                    className='rounded-md bg-primary px-8 py-6 text-lg font-semibold hover:bg-primary/90'
                                >
                                    {t('hero.ctaButton')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Problems Section */}
                <section ref={problemsSectionRef} id='problems' className='bg-neutral-100 w-full px-6 py-12 md:py-24'>
                    <div className='container px-4 md:px-6'>
                        <div className='grid grid-cols-1 items-center gap-8 md:grid-cols-2'>
                            <div className='relative h-[400px] w-full overflow-hidden rounded-lg shadow-xl'>
                                <img
                                    src='/vertical-cortando-salmon.webp'
                                    alt='Chef estresado'
                                    className='h-full w-full object-cover'
                                />
                            </div>
                            <div className='space-y-6'>
                                <h2 className='text-3xl font-bold tracking-tighter sm:text-4xl'>
                                    {t('problems.title')}{' '}
                                    <span className='text-primary'>{t('problems.highlight')}</span>
                                </h2>
                                <p className='text-neutral-700 md:text-lg'>{t('problems.description')}</p>
                                <div className='space-y-4'>
                                    <div className='flex items-center'>
                                        <X className='mr-3 h-6 w-6 flex-shrink-0 text-destructive' />
                                        <p className='text-neutral-700'>{t('problems.issue1')}</p>
                                    </div>
                                    <div className='flex items-center'>
                                        <X className='mr-3 h-6 w-6 flex-shrink-0 text-destructive' />
                                        <p className='text-neutral-700'>{t('problems.issue2')}</p>
                                    </div>
                                    <div className='flex items-center'>
                                        <X className='mr-3 h-6 w-6 flex-shrink-0 text-destructive' />
                                        <p className='text-neutral-700'>{t('problems.issue3')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Works Section */}
                <section ref={howItWorksSectionRef} id='how-it-works' className='w-full px-6 py-12 md:py-24'>
                    <div className='container px-4 md:px-6'>
                        <div className='mb-12 text-center'>
                            <h2 className='text-3xl font-bold tracking-tighter sm:text-4xl'>
                                {t('howItWorks.title')}{' '}
                                <span className='text-primary'>{t('howItWorks.highlight')}</span>
                            </h2>
                            <p className='text-neutral-700 mx-auto mt-4 max-w-[800px] md:text-lg'>
                                {t('howItWorks.description')}
                            </p>
                        </div>
                        <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader>
                                    <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                        <Mic className='h-6 w-6 text-primary' />
                                    </div>
                                    <CardTitle className='text-xl'>{t('howItWorks.feature1.title')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700'>"{t('howItWorks.feature1.description')}"</p>
                                </CardContent>
                            </Card>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader>
                                    <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                        <FileText className='h-6 w-6 text-primary' />
                                    </div>
                                    <CardTitle className='text-xl'>{t('howItWorks.feature2.title')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700'>"{t('howItWorks.feature2.description')}"</p>
                                </CardContent>
                            </Card>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader>
                                    <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                        <DollarSign className='h-6 w-6 text-primary' />
                                    </div>
                                    <CardTitle className='text-xl'>{t('howItWorks.feature3.title')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700'>"{t('howItWorks.feature3.description')}"</p>
                                </CardContent>
                            </Card>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader>
                                    <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                        <MessageSquare className='h-6 w-6 text-primary' />
                                    </div>
                                    <CardTitle className='text-xl'>{t('howItWorks.feature4.title')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700'>"{t('howItWorks.feature4.description')}"</p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section
                    ref={benefitsSectionRef}
                    id='benefits'
                    className='bg-neutral-100 relative w-full px-6 py-12 md:py-24'
                >
                    <div className='absolute inset-0 z-0 opacity-20'>
                        <img src='/plato-tataki.webp' alt='Plato de comida' className='h-full w-full object-cover' />
                    </div>
                    <div className='container relative z-10 px-4 md:px-6'>
                        <div className='mb-12 text-center'>
                            <h2 className='text-3xl font-bold tracking-tighter sm:text-4xl'>
                                {t('benefits.title')} <span className='text-primary'>{t('benefits.highlight')}</span>
                            </h2>
                        </div>
                        <div className='grid grid-cols-1 gap-8 md:ml-40 md:grid-cols-2'>
                            <div className='flex flex-col items-start'>
                                <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                    <Clock className='h-6 w-6 text-primary' />
                                </div>
                                <h3 className='mb-2 text-xl font-bold'>{t('benefits.time.title')}</h3>
                                <p className='text-neutral-700'>{t('benefits.time.description')}</p>
                            </div>
                            <div className='flex flex-col items-start'>
                                <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                    <TrendingUp className='h-6 w-6 text-primary' />
                                </div>
                                <h3 className='mb-2 text-xl font-bold'>{t('benefits.margins.title')}</h3>
                                <p className='text-neutral-700'>{t('benefits.margins.description')}</p>
                            </div>
                            <div className='flex flex-col items-start'>
                                <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                    <Headphones className='h-6 w-6 text-primary' />
                                </div>
                                <h3 className='mb-2 text-xl font-bold'>{t('benefits.support.title')}</h3>
                                <p className='text-neutral-700'>{t('benefits.support.description')}</p>
                            </div>
                            <div className='flex flex-col items-start'>
                                <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                    <PieChart className='h-6 w-6 text-primary' />
                                </div>
                                <h3 className='mb-2 text-xl font-bold'>{t('benefits.analytics.title')}</h3>
                                <p className='text-neutral-700'>{t('benefits.analytics.description')}</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Market Data Section */}
                <section ref={marketSectionRef} id='market' className='w-full py-12 md:py-24'>
                    <div className='container px-4 md:px-6'>
                        <div className='mb-12 text-center'>
                            <h2 className='text-3xl font-bold tracking-tighter sm:text-4xl'>
                                {t('market.title')} <span className='text-primary'>{t('market.highlight')}</span>
                            </h2>
                        </div>
                        <div className='grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-5'>
                            {[1, 2, 3, 4, 5].map(index => (
                                <Card
                                    key={index}
                                    className='border-none text-center shadow-lg transition-all duration-300 hover:shadow-xl'
                                >
                                    <CardHeader>
                                        <CardTitle className='text-3xl font-bold text-primary'>
                                            {t(`market.stat${index}`)}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className='text-neutral-700'>{t(`market.stat${index}Description`)}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Team Section */}
                <section ref={teamSectionRef} id='team' className='bg-neutral-100 w-full py-12 md:py-24'>
                    <div className='container px-4 md:px-6'>
                        <div className='mb-12 text-center'>
                            <h2 className='text-3xl font-bold tracking-tighter sm:text-4xl'>
                                {t('team.title')} <span className='text-primary'>{t('team.highlight')}</span>
                            </h2>
                            <p className='text-neutral-700 mx-auto mt-4 max-w-[800px] md:text-lg'>
                                {t('team.description')}
                            </p>
                        </div>
                        <div className='grid grid-cols-1 gap-8 md:grid-cols-2'>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader className='flex flex-row items-center gap-4'>
                                    <div className='flex h-16 w-16 items-center justify-center rounded-full bg-primary/10'>
                                        <span className='text-xl font-bold text-primary'>SC</span>
                                    </div>
                                    <div>
                                        <CardTitle>{t('team.member1.name')}</CardTitle>
                                        <CardDescription>{t('team.member1.role')}</CardDescription>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700'>{t('team.member1.description')}</p>
                                    <div className='mt-4 flex gap-2'>
                                        <img src='/annua.webp' alt='Annua' className='h-8 w-auto rounded-full' />
                                        <img src='/gula.webp' alt='Gula' className='h-8 w-auto rounded-full' />
                                        <img src='/bulbiza.webp' alt='Bulbiza' className='h-8 w-auto rounded-full' />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader className='flex flex-row items-center gap-4'>
                                    <div className='flex h-16 w-16 items-center justify-center rounded-full bg-primary/10'>
                                        <span className='text-xl font-bold text-primary'>JA</span>
                                    </div>
                                    <div>
                                        <CardTitle>{t('team.member2.name')}</CardTitle>
                                        <CardDescription>{t('team.member2.role')}</CardDescription>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700'>{t('team.member2.description')}</p>
                                    <div className='mt-4 flex gap-2'>
                                        <img src='/king.webp' alt='King' className='h-8 w-auto rounded-full' />
                                        <img src='/subbo.webp' alt='Subbo' className='h-8 w-auto rounded-full' />
                                        <img
                                            src='/Travelperk.webp'
                                            alt='TravelPerk'
                                            className='h-8 w-auto rounded-full'
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section ref={pricingSectionRef} id='pricing' className='w-full py-12 md:py-24'>
                    <div className='container px-4 md:px-6'>
                        <div className='mb-12 text-center'>
                            <h2 className='text-3xl font-bold tracking-tighter sm:text-4xl'>
                                {t('pricing.title')} <span className='text-primary'>{t('pricing.highlight')}</span>
                            </h2>
                        </div>
                        <div className='grid grid-cols-1 gap-8 md:grid-cols-3'>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader>
                                    <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                        <Home className='h-6 w-6 text-primary' />
                                    </div>
                                    <CardTitle>{t('pricing.micro.title')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700 mb-6'>{t('pricing.micro.description')}</p>
                                    <div className='space-y-4'>
                                        <div className='flex items-center gap-2'>
                                            <CheckCircle className='h-5 w-5 text-primary' />
                                            <span className='text-sm'>{t('pricing.micro.feature1')}</span>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                            <CheckCircle className='h-5 w-5 text-primary' />
                                            <span className='text-sm'>{t('pricing.micro.feature2')}</span>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={scrollToContact}
                                        className='mt-6 w-full rounded-md bg-primary hover:bg-primary/90'
                                    >
                                        {t('pricing.ctaButton')}
                                    </Button>
                                </CardContent>
                            </Card>
                            <Card className='border-none shadow-lg ring-2 ring-primary transition-all duration-300 hover:shadow-xl'>
                                <CardHeader>
                                    <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                        <Utensils className='h-6 w-6 text-primary' />
                                    </div>
                                    <CardTitle>{t('pricing.medium.title')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700 mb-6'>{t('pricing.medium.description')}</p>
                                    <div className='space-y-4'>
                                        <div className='flex items-center gap-2'>
                                            <CheckCircle className='h-5 w-5 text-primary' />
                                            <span className='text-sm'>{t('pricing.medium.feature1')}</span>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                            <CheckCircle className='h-5 w-5 text-primary' />
                                            <span className='text-sm'>{t('pricing.medium.feature2')}</span>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={scrollToContact}
                                        className='mt-6 w-full rounded-md bg-primary hover:bg-primary/90'
                                    >
                                        {t('pricing.ctaButton')}
                                    </Button>
                                </CardContent>
                            </Card>
                            <Card className='border-none shadow-lg transition-all duration-300 hover:shadow-xl'>
                                <CardHeader>
                                    <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                                        <Building className='h-6 w-6 text-primary' />
                                    </div>
                                    <CardTitle>{t('pricing.enterprise.title')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-neutral-700 mb-6'>{t('pricing.enterprise.description')}</p>
                                    <div className='space-y-4'>
                                        <div className='flex items-center gap-2'>
                                            <CheckCircle className='h-5 w-5 text-primary' />
                                            <span className='text-sm'>{t('pricing.enterprise.feature1')}</span>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                            <CheckCircle className='h-5 w-5 text-primary' />
                                            <span className='text-sm'>{t('pricing.enterprise.feature2')}</span>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={scrollToContact}
                                        className='mt-6 w-full rounded-md bg-primary hover:bg-primary/90'
                                    >
                                        {t('pricing.ctaButton')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section id='contact' ref={formSectionRef} className='w-full px-6 pb-12 md:pb-24'>
                    <div className='container px-4 md:px-6'>
                        <div className='mx-auto max-w-[600px] text-center'>
                            <h2 className='mb-6 text-3xl font-bold tracking-tighter sm:text-4xl'>
                                {t('contact.title')} <span className='text-primary'>{t('contact.highlight')}</span>{' '}
                                {t('contact.subtitle')}
                            </h2>
                            <LeadForm landingPage='business' />

                            <h3 className='mb-4 mt-8 text-2xl font-semibold'>{t('contact.getInTouch')}</h3>

                            <div className='mb-8 flex flex-col justify-center gap-6 sm:flex-row'>
                                <div className='flex items-center'>
                                    <Mail className='mr-2 h-5 w-5' />
                                    <a href={`mailto:${t('contact.email')}`} className='hover:text-primary'>
                                        {t('contact.email')}
                                    </a>
                                </div>
                                <div className='flex items-center'>
                                    <PhoneCall className='mr-2 h-5 w-5' />
                                    <a href={toOfficialSalesTelHref()} className='hover:text-primary'>
                                        {OFFICIAL_SALES_PHONE}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <NewsletterSubscribe variant='landing' source='landing/business' />

            <Footer landingPage='business' />
        </div>
    )
}
